import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Sparkles } from "lucide-react";
import useIsMobile from "components/hooks/useIsMobile";
import {
  useListAssignmentCandidates,
  useSendAssignmentOpportunities,
} from "services/signup";
import {
  AssignmentCandidate,
  SignupSubmission,
} from "protogen/signup_service_pb";
import SearchBar from "components/forum/SearchBar";
import GenerateMatches, {
  MatchReasonMap,
} from "components/matchmaking/GenerateMatches";
import AdvisorRow from "./AdvisorRow";

const AdvisorListWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        borderRadius: "12px",
        border: "1px solid #D4D4D4",
      }}
    >
      {children}
    </Box>
  );
};

type Props = {
  lead?: SignupSubmission;
  familyRef?: string;
  onStarted: () => void;
};

export default ({ lead, familyRef, onStarted }: Props) => {
  const isMobile = useIsMobile();
  const [advisors, setAdvisors] = useState<AssignmentCandidate[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showOnlyNewAdvisors, setShowOnlyNewAdvisors] = useState(false);
  const [generateMatchesOpen, setGenerateMatchesOpen] = useState(false);
  const [generateMatchesRunning, setGenerateMatchesRunning] = useState(false);
  const [matchReasonMap, setMatchReasonMap] = useState<MatchReasonMap>({});
  const { request: sendAssignmentOpportunities } =
    useSendAssignmentOpportunities();
  const { request: fetchAssignmentCandidates, data: assignmentCandidatesData } =
    useListAssignmentCandidates();
  useEffect(() => {
    fetchAssignmentCandidates({
      leadRef: lead?.ref || "",
      familyRef: familyRef || "",
      sortByDistance: true,
      includeMMForm: true,
    });
  }, []);

  useEffect(() => {
    setAdvisors([]);
  }, [lead]);

  // Get existing assignments and their advisor refs
  const existingAssignments = lead?.assignments || [];

  // Get the full list of advisors for looking up existing assignments
  const allCandidates = assignmentCandidatesData?.candidates || [];

  const filteredCandidates =
    assignmentCandidatesData?.candidates.filter((candidate) => {
      const mmForm = candidate.mmForm;
      const matchesSearch =
        candidate.advisor?.firstName
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        candidate.advisor?.lastName
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        candidate.advisor?.displayName
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        candidate.advisor?.city
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        candidate.advisor?.state
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        candidate.superPowers
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        candidate.advisor?.blurbText
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (mmForm && mmForm.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesNewAdvisorFilter =
        !showOnlyNewAdvisors || candidate.advisor?.families?.length === 0;

      return matchesSearch && matchesNewAdvisorFilter;
    }) || [];

  const onSend = async () => {
    if (advisors.length === 0) return;
    if (!lead) return;
    await sendAssignmentOpportunities({
      submissionRef: lead.ref,
      advisorRefs: advisors.map((a) => a.advisor?.ref || ""),
      // Only append to existing if there are existing assignments, otherwise we want an error
      // to prevent two users from matching simultaneously.
      appendToExisting: lead.assignments.length > 0,
    });
    onStarted();
  };

  const handleSelect = (advisor: AssignmentCandidate) => {
    if (!advisors.some((a) => a.advisor?.ref === advisor.advisor?.ref)) {
      setAdvisors([...advisors, advisor]);
    }
  };

  const handleRemove = (advisor: AssignmentCandidate) => {
    setAdvisors(
      advisors.filter((a) => a.advisor?.ref !== advisor.advisor?.ref),
    );
  };

  const moveUp = (index: number) => {
    if (index > 0) {
      const newAdvisors = [...advisors];
      const temp = newAdvisors[index];
      newAdvisors[index] = newAdvisors[index - 1];
      newAdvisors[index - 1] = temp;
      setAdvisors(newAdvisors);
    }
  };

  const moveDown = (index: number) => {
    if (index < advisors.length - 1) {
      const newAdvisors = [...advisors];
      const temp = newAdvisors[index];
      newAdvisors[index] = newAdvisors[index + 1];
      newAdvisors[index + 1] = temp;
      setAdvisors(newAdvisors);
    }
  };
  const needsMatchmaking =
    familyRef ||
    (lead &&
      (lead.pipelineStatus === "awaiting-match" ||
        lead.pipelineStatus === "signup-complete"));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "42px" }}>
      {/* Selected Advisors Table */}
      {lead && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box
              sx={{
                display: "flex",
                gap: "8px",
                padding: "12px 0",
              }}
            >
              <Typography variant="h3">
                {isMobile ? "Selected" : "Selected Advisors"}
              </Typography>
              <Typography variant="bodyHeavy" color="text.tertiary">
                {existingAssignments.length + advisors.length}
              </Typography>
            </Box>

            {needsMatchmaking && (
              <Button
                variant="contained"
                onClick={onSend}
                disabled={advisors.length === 0}
                sx={{ alignSelf: "flex-end" }}
              >
                {isMobile ? "Share" : "Share opportunities"}
              </Button>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: "100%",
            }}
          >
            {existingAssignments.length === 0 && advisors.length === 0 ? (
              <Box sx={{ width: "100%", textAlign: "center", py: 4 }}>
                <Typography variant="body" sx={{ color: "text.secondary" }}>
                  No advisors selected
                </Typography>
              </Box>
            ) : (
              <>
                {/* Existing Assignments */}
                <AdvisorListWrapper>
                  {existingAssignments.map((assignment, index) => {
                    const candidate = allCandidates.find(
                      (c) => c.advisor?.ref === assignment.advisor?.ref,
                    );
                    if (!candidate) return null;

                    return (
                      <AdvisorRow
                        key={assignment.advisor?.ref}
                        candidate={candidate}
                        assignment={assignment}
                        actionType="existing"
                        isLast={
                          advisors.length === 0 &&
                          index === existingAssignments.length - 1
                        }
                        lead={lead}
                        generateMatchesRunning={generateMatchesRunning}
                        matchReason={matchReasonMap[assignment.advisor?.ref]}
                        refresh={onStarted}
                      />
                    );
                  })}
                  {/* Newly Selected Advisors */}
                  {advisors.map((candidate, index) => (
                    <AdvisorRow
                      key={candidate.advisor?.ref}
                      candidate={candidate}
                      actionType="new"
                      advisors={advisors}
                      index={index}
                      moveUp={moveUp}
                      moveDown={moveDown}
                      handleRemove={handleRemove}
                      isLast={index === advisors.length - 1}
                      lead={lead}
                      generateMatchesRunning={generateMatchesRunning}
                      matchReason={matchReasonMap[candidate.advisor?.ref]}
                    />
                  ))}
                </AdvisorListWrapper>
              </>
            )}
          </Box>
        </Box>
      )}
      {/* Available Advisors Table */}
      {needsMatchmaking && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "24px",
          }}
        >
          <Box
            display="flex"
            width="100%"
            gap={isMobile ? "12px" : "24px"}
            {...(isMobile && { flexDirection: "column" })}
            {...(!isMobile && {
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            })}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "start",
              }}
            >
              <Box sx={{ display: "flex", gap: "8px" }}>
                <Typography variant="h3">Advisors </Typography>
                <Typography variant="bodyHeavy" color="text.tertiary">
                  {
                    filteredCandidates.filter(
                      (candidate) =>
                        !existingAssignments.some(
                          (a) => a.advisor?.ref === candidate.advisor?.ref,
                        ) &&
                        !advisors.some(
                          (a) => a.advisor?.ref === candidate.advisor?.ref,
                        ),
                    ).length
                  }
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showOnlyNewAdvisors}
                    onChange={(e) => setShowOnlyNewAdvisors(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "14px", color: "text.secondary" }}
                  >
                    Only new advisors
                  </Typography>
                }
                sx={{ margin: 0, marginLeft: "-9px" }}
              />
            </Box>
            <Box display="flex" gap={2} alignItems="center">
              {lead && (
                <Button
                  variant="outlined"
                  startIcon={<Sparkles />}
                  onClick={() => setGenerateMatchesOpen(!generateMatchesOpen)}
                  sx={{
                    height: "40px", // Match SearchBar height
                    whiteSpace: "nowrap", // Prevent text wrapping
                  }}
                >
                  Generate matches
                </Button>
              )}
              <SearchBar
                initialQuery={searchTerm}
                onQuery={setSearchTerm}
                expand={true}
                autofocus={true}
                onKeyPress={true}
              />
            </Box>
          </Box>
          {lead && (
            <GenerateMatches
              lead={lead}
              open={generateMatchesOpen}
              setOpen={setGenerateMatchesOpen}
              setRunning={setGenerateMatchesRunning}
              setMatches={setMatchReasonMap}
              runEagerly={true}
            />
          )}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: "100%",
            }}
          >
            {/* Available Advisors */}
            <AdvisorListWrapper>
              {filteredCandidates
                .filter(
                  (candidate) =>
                    !existingAssignments.some(
                      (c) => c.advisor?.ref === candidate.advisor?.ref,
                    ) &&
                    !advisors.some(
                      (c) => c.advisor?.ref === candidate.advisor?.ref,
                    ),
                )
                .map((candidate, index) => (
                  <AdvisorRow
                    key={candidate.advisor?.ref}
                    candidate={candidate}
                    actionType="available"
                    handleSelect={handleSelect}
                    isLast={index === filteredCandidates.length - 1}
                    lead={lead}
                    generateMatchesRunning={generateMatchesRunning}
                    matchReason={matchReasonMap[candidate.advisor?.ref || ""]}
                  />
                ))}
            </AdvisorListWrapper>

            {/* Empty State */}
            {filteredCandidates.filter(
              (candidate) =>
                !existingAssignments.some(
                  (a) => a.advisor?.ref === candidate.advisor?.ref,
                ) &&
                !advisors.some(
                  (c) => c.advisor?.ref === candidate.advisor?.ref,
                ),
            ).length === 0 && (
              <Box sx={{ width: "100%", textAlign: "center", py: 4 }}>
                <Typography variant="body" sx={{ color: "text.secondary" }}>
                  No advisors match your filters
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};
